// Реестр корректирующих действий (модуль quality-records, IT-P09): список с
// фильтрами и ближайшим сроком, карточка с анализом, действиями и проверкой
// результативности.  Чтение — manufacturing_order.read, ведение —
// quality.manage, утверждение существенного КД — quality.approve.
// ?id=<КД> открывает карточку, ?nc=<несоответствие> — новое КД по нему.

import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { useQualityError } from "@/components/OrderNonconformities";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import { useAuth } from "@/lib/AuthContext";
import {
  qualityApi,
  type CorrectiveAction,
  type CorrectiveActionCard,
  type CorrectiveActionPatch,
  type CorrectiveActionSource,
  type CorrectiveActionStatus,
} from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";
const STATUSES: CorrectiveActionStatus[] = ["OPEN", "IN_PROGRESS", "VERIFICATION", "CLOSED"];
const SOURCES: CorrectiveActionSource[] = [
  "NONCONFORMITY",
  "CUSTOMER_COMPLAINT",
  "INTERNAL_AUDIT",
  "EXTERNAL_AUDIT",
  "TREND",
  "SUPPLIER",
];
const KEY = ["quality", "corrective-actions"] as const;

export function CaStatus({ a }: { a: Pick<CorrectiveAction, "status" | "significant" | "approved_at"> }) {
  const { t } = useTranslation();
  const cls = a.status === "CLOSED" ? "pill-ok" : a.status === "VERIFICATION" ? "pill-info" : "pill-warn";
  return (
    <span className="inline-flex gap-1">
      <span className={`pill text-xs ${cls}`}>{t(`quality.caStatus.${a.status}`)}</span>
      {a.significant && (
        <span className={`pill text-xs ${a.approved_at ? "pill-accent" : "pill-danger"}`}>{t("quality.ca.significantPill")}</span>
      )}
    </span>
  );
}

export default function CorrectiveActions() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("id");
  const ncId = params.get("nc");
  const [status, setStatus] = useState<CorrectiveActionStatus | "">("");
  const [source, setSource] = useState<CorrectiveActionSource | "">("");
  const [overdue, setOverdue] = useState(false);
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: [...KEY, "list", { status, source, overdue, page }],
    queryFn: () =>
      qualityApi.correctiveActions({
        status: status || undefined,
        source: source || undefined,
        overdue: overdue || undefined,
        page,
        page_size: 50,
      }),
  });
  const showCreate = hasPermission("quality.manage") && (creating || !!ncId);

  return (
    <>
      <PageHeader eyebrow={t("quality.eyebrow")} title={t("quality.ca.title")} description={t("quality.ca.description")} />

      <div className="card p-3 mt-3 flex flex-wrap items-center gap-2">
        {(["", ...STATUSES] as const).map((s) => (
          <button
            key={s || "all"}
            className={`btn btn-sm ${status === s ? "btn-primary" : ""}`}
            onClick={() => {
              setStatus(s);
              setPage(1);
            }}
          >
            {s ? t(`quality.caStatus.${s}`) : t("quality.ca.filterAll")}
          </button>
        ))}
        <select
          className="px-3 py-1.5 rounded-md bg-surface border border-border text-sm"
          value={source}
          onChange={(e) => {
            setSource(e.target.value as CorrectiveActionSource | "");
            setPage(1);
          }}
        >
          <option value="">{t("quality.ca.allSources")}</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {t(`quality.caSource.${s}`)}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={overdue}
            onChange={(e) => {
              setOverdue(e.target.checked);
              setPage(1);
            }}
          />
          {t("quality.ca.overdueOnly")}
        </label>
        {hasPermission("quality.manage") && !showCreate && (
          <button className="btn btn-sm btn-primary ml-auto" onClick={() => setCreating(true)}>
            {t("quality.ca.new")}
          </button>
        )}
      </div>

      {showCreate && (
        <CreateForm
          key={ncId ?? "new"}
          ncId={ncId}
          onDone={(id) => {
            setCreating(false);
            setParams(id ? { id } : {});
          }}
        />
      )}

      {list.isLoading && <LoadingState />}
      {list.isError && <ErrorState error={list.error} />}
      {list.data && list.data.items.length === 0 && (
        <div className="card p-8 mt-4 text-center text-text-muted">{t("quality.ca.empty")}</div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4 items-start">
        {list.data && list.data.items.length > 0 && (
          <section className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-alt text-text-muted">
                <tr>
                  <th className="px-3 py-2 text-left">{t("quality.ca.colNumber")}</th>
                  <th className="px-3 py-2 text-left">{t("quality.ca.colSource")}</th>
                  <th className="px-3 py-2 text-left">{t("quality.ca.colTitle")}</th>
                  <th className="px-3 py-2 text-left">{t("quality.ca.colStatus")}</th>
                  <th className="px-3 py-2 text-left">{t("quality.ca.colNextDue")}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((a) => (
                  <tr
                    key={a.id}
                    className={`border-t border-border cursor-pointer hover:bg-surface ${a.id === selectedId ? "bg-surface" : ""}`}
                    onClick={() => setParams({ id: a.id })}
                  >
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{a.number}</td>
                    <td className="px-3 py-2 text-xs">{t(`quality.caSource.${a.source}`)}</td>
                    <td className="px-3 py-2 max-w-xs truncate">{a.title}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <CaStatus a={a} />
                    </td>
                    <td className={`px-3 py-2 font-mono text-xs whitespace-nowrap ${a.overdue ? "text-danger font-semibold" : ""}`}>
                      {a.next_due ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        {selectedId ? (
          <CardLoader id={selectedId} />
        ) : (
          list.data &&
          list.data.items.length > 0 && <div className="card p-6 text-sm text-text-muted">{t("quality.ca.select")}</div>
        )}
      </div>

      {list.data && list.data.pagination.pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-text-muted">
            {t("common.pageOf", {
              page: list.data.pagination.page,
              pages: list.data.pagination.pages,
              total: list.data.pagination.total,
            })}
          </span>
          <div className="flex gap-2">
            <button className="btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ←
            </button>
            <button className="btn" disabled={page >= list.data.pagination.pages} onClick={() => setPage((p) => p + 1)}>
              →
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function CreateForm({ ncId, onDone }: { ncId: string | null; onDone: (id?: string) => void }) {
  const nc = useQuery({
    queryKey: ["quality", "nonconformities", "one", ncId],
    queryFn: () => qualityApi.nonconformity(ncId!),
    enabled: !!ncId,
  });
  if (ncId && !nc.data) return nc.isError ? <ErrorState error={nc.error} /> : <LoadingState />;
  return <CreateFields nc={nc.data ?? null} onDone={onDone} />;
}

/** Из несоответствия — источник «несоответствие», суть и описание из него. */
function CreateFields({
  nc,
  onDone,
}: {
  nc: { id: string; number: string; description: string } | null;
  onDone: (id?: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const errText = useQualityError();
  const [source, setSource] = useState<CorrectiveActionSource>("NONCONFORMITY");
  const [title, setTitle] = useState(nc ? nc.number : "");
  const [description, setDescription] = useState(nc?.description ?? "");
  const [correction, setCorrection] = useState("");
  const create = useMutation({
    mutationFn: () =>
      qualityApi.createCorrectiveAction({
        source,
        title: title.trim(),
        description: description.trim(),
        ...(correction.trim() ? { correction: correction.trim() } : {}),
        ...(nc ? { nonconformity_ids: [nc.id] } : {}),
      }),
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: KEY });
      onDone(a.id);
    },
  });

  return (
    <section className="card p-5 mt-4 space-y-3">
      <h2 className="text-lg">{t("quality.ca.new")}</h2>
      {nc && (
        <p className="text-sm text-text-muted">
          {t("quality.ca.nonconformities")}: <span className="font-mono">{nc.number}</span>
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[240px,1fr] gap-3">
        <select className={INPUT_CLS} value={source} onChange={(e) => setSource(e.target.value as CorrectiveActionSource)}>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {t(`quality.caSource.${s}`)}
            </option>
          ))}
        </select>
        <input className={INPUT_CLS} placeholder={t("quality.ca.titleField")} value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <textarea
        className={INPUT_CLS}
        rows={3}
        placeholder={t("quality.ca.whatHappened")}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <textarea
        className={INPUT_CLS}
        rows={2}
        placeholder={t("quality.ca.correctionHint")}
        value={correction}
        onChange={(e) => setCorrection(e.target.value)}
      />
      {create.isError && <ErrorBanner onDismiss={() => create.reset()}>{errText(create.error)}</ErrorBanner>}
      <div className="flex gap-2">
        <button
          className="btn btn-sm btn-primary"
          disabled={!title.trim() || !description.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {t("quality.ca.create")}
        </button>
        <button className="btn btn-sm" onClick={() => onDone()}>
          {t("common.cancel")}
        </button>
      </div>
    </section>
  );
}

function CardLoader({ id }: { id: string }) {
  const card = useQuery({ queryKey: [...KEY, "card", id], queryFn: () => qualityApi.correctiveAction(id) });
  if (card.isLoading) return <LoadingState />;
  if (card.isError) return <ErrorState error={card.error} />;
  return card.data ? <Card key={card.data.id} a={card.data} /> : null;
}

/** Поля анализа, которые правятся одной кнопкой «Сохранить». */
type Analysis = {
  correction: string;
  root_cause: string;
  risk_update: string;
  moc_required: boolean;
  moc_ref: string;
  similar_assessment: string;
  effectiveness_due: string;
  significant: boolean;
};

function analysisOf(a: CorrectiveActionCard): Analysis {
  return {
    correction: a.correction ?? "",
    root_cause: a.root_cause ?? "",
    risk_update: a.risk_update ?? "",
    moc_required: a.moc_required,
    moc_ref: a.moc_ref ?? "",
    similar_assessment: a.similar_assessment ?? "",
    effectiveness_due: a.effectiveness_due ?? "",
    significant: a.significant,
  };
}

function Card({ a }: { a: CorrectiveActionCard }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const errText = useQualityError();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("quality.manage") && a.status !== "CLOSED";
  const itemsEditable = canEdit && (a.status === "OPEN" || a.status === "IN_PROGRESS");

  const [form, setForm] = useState<Analysis>(() => analysisOf(a));
  const [verifyNote, setVerifyNote] = useState("");
  const set = <K extends keyof Analysis>(k: K, v: Analysis[K]) => setForm((f) => ({ ...f, [k]: v }));

  const done = () => qc.invalidateQueries({ queryKey: KEY });
  const save = useMutation({
    mutationFn: () => {
      const before = analysisOf(a);
      const patch: CorrectiveActionPatch = {};
      for (const k of Object.keys(form) as Array<keyof Analysis>) {
        if (form[k] === before[k]) continue;
        const v = form[k];
        // Пустая строка — поле очищено.
        Object.assign(patch, { [k]: typeof v === "string" ? v.trim() || null : v });
      }
      return qualityApi.updateCorrectiveAction(a.id, patch);
    },
    onSuccess: (saved) => {
      setForm(analysisOf(saved));
      done();
    },
  });
  const approve = useMutation({ mutationFn: () => qualityApi.approveCorrectiveAction(a.id), onSuccess: done });
  const move = useMutation({
    mutationFn: (s: CorrectiveActionStatus) => qualityApi.setCorrectiveActionStatus(a.id, s),
    onSuccess: done,
  });
  const verify = useMutation({
    mutationFn: (result: "EFFECTIVE" | "NOT_EFFECTIVE") =>
      qualityApi.verifyCorrectiveAction(a.id, { result, ...(verifyNote.trim() ? { note: verifyNote.trim() } : {}) }),
    onSuccess: () => {
      setVerifyNote("");
      done();
    },
  });
  const err = save.error ?? approve.error ?? move.error ?? verify.error;
  const dirty = JSON.stringify(form) !== JSON.stringify(analysisOf(a));

  const text = (k: "correction" | "root_cause" | "risk_update" | "similar_assessment", placeholder?: string) =>
    canEdit ? (
      <textarea className={INPUT_CLS} rows={2} placeholder={placeholder} value={form[k]} onChange={(e) => set(k, e.target.value)} />
    ) : (
      <p className="text-sm whitespace-pre-wrap">{a[k] ?? "—"}</p>
    );

  return (
    <section className="card p-5 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-mono">{a.number}</h2>
          <p className="text-sm">{a.title}</p>
          <p className="text-xs text-text-muted">
            {t(`quality.caSource.${a.source}`)} · {t("quality.ca.opened")}: {a.opener_name ?? "—"} · {formatDateTime(a.opened_at)}
          </p>
        </div>
        <CaStatus a={a} />
      </div>

      {err && (
        <ErrorBanner
          onDismiss={() => {
            save.reset();
            approve.reset();
            move.reset();
            verify.reset();
          }}
        >
          {errText(err)}
        </ErrorBanner>
      )}

      <Section title={t("quality.ca.whatHappened")}>
        <p className="text-sm whitespace-pre-wrap">{a.description}</p>
        {a.nonconformities.length > 0 && (
          <ul className="text-sm mt-2">
            {a.nonconformities.map((n) => (
              <li key={n.id}>
                <span className="font-mono">{n.number}</span>
                <span className="text-text-muted"> · {t(`quality.status.${n.status}`)} · </span>
                {n.description}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={t("quality.ca.correction")} hint={t("quality.ca.correctionHint")}>
        {text("correction")}
      </Section>

      <Section title={t("quality.ca.rootCause")}>{text("root_cause")}</Section>

      <Section title={t("quality.ca.significantTitle")}>
        {canEdit ? (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.significant} onChange={(e) => set("significant", e.target.checked)} />
            {t("quality.ca.significant")}
          </label>
        ) : (
          <p className="text-sm">{a.significant ? t("quality.ca.significant") : "—"}</p>
        )}
        {a.significant && (
          <p className="text-sm mt-1">
            {a.approved_at
              ? `${t("quality.ca.approved")}: ${a.approver_name ?? "—"} · ${formatDateTime(a.approved_at)}`
              : t("quality.ca.notApproved")}
          </p>
        )}
        {a.significant && !a.approved_at && a.status !== "CLOSED" && hasPermission("quality.approve") && (
          <button className="btn btn-sm btn-primary mt-2" disabled={approve.isPending} onClick={() => approve.mutate()}>
            {t("quality.ca.approve")}
          </button>
        )}
      </Section>

      <Section title={t("quality.ca.actions")}>
        <Items a={a} editable={itemsEditable} />
      </Section>

      <Section title={t("quality.ca.risks")}>
        <div className="space-y-2">
          {text("risk_update", t("quality.ca.riskUpdate"))}
          {canEdit ? (
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.moc_required} onChange={(e) => set("moc_required", e.target.checked)} />
                {t("quality.ca.mocRequired")}
              </label>
              {form.moc_required && (
                <input
                  className="flex-1 min-w-48 px-3 py-2 rounded-md bg-surface border border-border"
                  placeholder={t("quality.ca.mocRef")}
                  value={form.moc_ref}
                  onChange={(e) => set("moc_ref", e.target.value)}
                />
              )}
            </div>
          ) : (
            a.moc_required && (
              <p className="text-sm">
                {t("quality.ca.mocRequired")}: {a.moc_ref ?? "—"}
              </p>
            )
          )}
        </div>
      </Section>

      <Section title={t("quality.ca.similar")} hint={t("quality.ca.similarHint")}>
        {text("similar_assessment")}
      </Section>

      <Section title={t("quality.ca.effectiveness")}>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-text-muted">{t("quality.ca.effectivenessDue")}:</span>
            {canEdit ? (
              <input
                className="px-3 py-1.5 rounded-md bg-surface border border-border"
                type="date"
                value={form.effectiveness_due}
                onChange={(e) => set("effectiveness_due", e.target.value)}
              />
            ) : (
              <span className="font-mono">{a.effectiveness_due ?? "—"}</span>
            )}
          </div>
          {a.effectiveness_result && (
            <p>
              <span className={`pill text-xs ${a.effectiveness_result === "EFFECTIVE" ? "pill-ok" : "pill-danger"}`}>
                {t(`quality.effectiveness.${a.effectiveness_result}`)}
              </span>{" "}
              {a.verifier_name ?? "—"} · {formatDateTime(a.verified_at)}
              {a.effectiveness_note && <span className="block whitespace-pre-wrap mt-1">{a.effectiveness_note}</span>}
            </p>
          )}
          {canEdit && a.status === "VERIFICATION" && (
            <div className="space-y-2 border-t border-border pt-3">
              <textarea
                className={INPUT_CLS}
                rows={2}
                placeholder={t("quality.ca.effectivenessNote")}
                value={verifyNote}
                onChange={(e) => setVerifyNote(e.target.value)}
              />
              <div className="flex gap-2">
                <button className="btn btn-sm" disabled={verify.isPending} onClick={() => verify.mutate("EFFECTIVE")}>
                  {t("quality.effectiveness.EFFECTIVE")}
                </button>
                <button className="btn btn-sm" disabled={verify.isPending} onClick={() => verify.mutate("NOT_EFFECTIVE")}>
                  {t("quality.effectiveness.NOT_EFFECTIVE")}
                </button>
              </div>
            </div>
          )}
          {a.closed_at && (
            <p>
              {t("quality.ca.closed")}: {formatDateTime(a.closed_at)}
            </p>
          )}
        </div>
      </Section>

      {canEdit && (
        <div className="flex flex-wrap gap-2 border-t border-border pt-4">
          <button className="btn btn-sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {t("quality.ca.save")}
          </button>
          {a.status === "OPEN" && (
            <button className="btn btn-sm btn-primary" disabled={dirty || move.isPending} onClick={() => move.mutate("IN_PROGRESS")}>
              {t("quality.ca.toInProgress")}
            </button>
          )}
          {a.status === "IN_PROGRESS" && (
            <button className="btn btn-sm btn-primary" disabled={dirty || move.isPending} onClick={() => move.mutate("VERIFICATION")}>
              {t("quality.ca.toVerification")}
            </button>
          )}
          {a.status === "VERIFICATION" && a.effectiveness_result === "EFFECTIVE" && (
            <button className="btn btn-sm btn-primary" disabled={move.isPending} onClick={() => move.mutate("CLOSED")}>
              {t("quality.ca.close")}
            </button>
          )}
          {dirty && <span className="text-xs text-text-muted self-center">{t("quality.ca.saveFirst")}</span>}
        </div>
      )}
    </section>
  );
}

function Items({ a, editable }: { a: CorrectiveActionCard; editable: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const errText = useQualityError();
  const [description, setDescription] = useState("");
  const [responsible, setResponsible] = useState("");
  const [due, setDue] = useState("");

  const done = () => qc.invalidateQueries({ queryKey: KEY });
  const add = useMutation({
    mutationFn: () =>
      qualityApi.addCorrectiveActionItem(a.id, {
        description: description.trim(),
        responsible: responsible.trim(),
        due_date: due,
      }),
    onSuccess: () => {
      setDescription("");
      setResponsible("");
      setDue("");
      done();
    },
  });
  const mark = useMutation({
    mutationFn: (v: { itemId: string; done: boolean; note?: string }) =>
      qualityApi.updateCorrectiveActionItem(a.id, v.itemId, {
        done: v.done,
        ...(v.note !== undefined ? { done_note: v.note || null } : {}),
      }),
    onSuccess: done,
  });
  const err = add.error ?? mark.error;

  return (
    <div className="space-y-3">
      {a.items.length === 0 ? (
        <p className="text-sm text-text-muted">{t("quality.ca.noItems")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-text-muted">
              <tr>
                <th className="py-1 pr-3 text-left">{t("quality.ca.itemDescription")}</th>
                <th className="py-1 pr-3 text-left">{t("quality.ca.itemResponsible")}</th>
                <th className="py-1 pr-3 text-left">{t("quality.ca.itemDue")}</th>
                <th className="py-1 text-left">{t("quality.ca.itemDone")}</th>
              </tr>
            </thead>
            <tbody>
              {a.items.map((i) => (
                <tr key={i.id} className="border-t border-border align-top">
                  <td className="py-1.5 pr-3 whitespace-pre-wrap">{i.description}</td>
                  <td className="py-1.5 pr-3">{i.responsible}</td>
                  <td className={`py-1.5 pr-3 font-mono text-xs whitespace-nowrap ${i.overdue ? "text-danger font-semibold" : ""}`}>
                    {i.due_date}
                  </td>
                  <td className="py-1.5">
                    {i.done_at ? (
                      <span className="text-xs">
                        ✓ {formatDateTime(i.done_at)}
                        {i.done_note && <span className="block text-text-muted">{i.done_note}</span>}
                      </span>
                    ) : (
                      !editable && "—"
                    )}
                    {editable && (
                      <button
                        className="btn btn-sm mt-1"
                        disabled={mark.isPending}
                        onClick={() => {
                          if (i.done_at) {
                            mark.mutate({ itemId: i.id, done: false });
                            return;
                          }
                          const note = window.prompt(t("quality.ca.itemNote"));
                          if (note !== null) mark.mutate({ itemId: i.id, done: true, note: note.trim() });
                        }}
                      >
                        {i.done_at ? t("quality.ca.undo") : t("quality.ca.markDone")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {err && (
        <ErrorBanner
          onDismiss={() => {
            add.reset();
            mark.reset();
          }}
        >
          {errText(err)}
        </ErrorBanner>
      )}

      {editable && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr,180px,150px,auto] gap-2">
          <input className={INPUT_CLS} placeholder={t("quality.ca.itemDescription")} value={description} onChange={(e) => setDescription(e.target.value)} />
          <input className={INPUT_CLS} placeholder={t("quality.ca.itemResponsible")} value={responsible} onChange={(e) => setResponsible(e.target.value)} />
          <input className={INPUT_CLS} type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          <button
            className="btn btn-sm"
            disabled={!description.trim() || !responsible.trim() || !due || add.isPending}
            onClick={() => add.mutate()}
          >
            {t("quality.ca.addItem")}
          </button>
        </div>
      )}
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold mb-1">{title}</h3>
      {hint && <p className="text-xs text-text-muted mb-1">{hint}</p>}
      {children}
    </div>
  );
}
